import { useMemo } from 'react';

import {
    useGetBetLimits,
    useGetDepositLimits,
    useGetForcedLossLimits,
    useGetGameHistory,
    useGetLossLimits,
    useGetPlayerLimitsInfo,
    useGetPlayerSelfExclusion,
    useSetBetLimits,
    useSetDepositLimits,
    useSetLossLimits,
    useSetPlayerSelfExclusion
} from 'server';

export const useResponsibleGamingPublicAPI = () => {
    const getDepositLimits = useGetDepositLimits();
    const getForcedLossLimits = useGetForcedLossLimits();
    const getGameHistory = useGetGameHistory();
    const getLossLimits = useGetLossLimits();
    const getBetLimits = useGetBetLimits();
    const setLossLimits = useSetLossLimits();
    const setBetLimits = useSetBetLimits();
    const getPlayerLimitsInfo = useGetPlayerLimitsInfo();
    const getPlayerSelfExclusion = useGetPlayerSelfExclusion();
    const setDepositLimits = useSetDepositLimits();
    const setPlayerSelfExclusion = useSetPlayerSelfExclusion();

    return useMemo(() => ({
        /**
         * Return information about current deposit limits. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/ResponsibleGamingService/operations/GetPlayerDepositLimitGalaxyNew.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{currentLimits: Object, waitingLimits: Object, allowedLimits: string[]}>}
         * @example
         * Playtech.API.responsibleGaming.getDepositLimits().then((data) => { ... }));
         */
        getDepositLimits,
        /**
         * Return information about current loss limits. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/ResponsibleGamingService/operations/GetPlayerLossLimits.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{limits: Object}>}
         * @example
         * Playtech.API.responsibleGaming.getLossLimits().then((data) => { ... }));
         */
        getLossLimits,
        /**
         * Return information about current bet limits. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/ResponsibleGamingService/operations/GetPlayerBetLimits.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{limits: Object}>}
         * @example
         * Playtech.API.responsibleGaming.getBetLimits().then((data) => { ... }));
         */
        getBetLimits,
        /**
         * Return information about current forced loss limits. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/ResponsibleGamingService/operations/GetPlayerForcedLossLimits.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{limits: Object}>}
         * @example
         * Playtech.API.responsibleGaming.getForcedLossLimits().then((data) => { ... }));
         */
        getForcedLossLimits,
        /**
         * Return information about current self-exclusion. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/ResponsibleGamingService/operations/GetPlayerSelfExclusion.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{Object}>} {allowed: boolean, disableDeposits: boolean, crossProduct: boolean, products: string[], periods: string[], endTypes: string[], panicButtonInterval: number, minEndDate: string, productSettings: object[]}
         * @example
         * Playtech.API.responsibleGaming.getPlayerSelfExclusion().then((data) => { ... }));
         */
        getPlayerSelfExclusion,
        /**
         * Return information about all current limits. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/ResponsibleGamingService/operations/GetPlayerLimitsInfo.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{Object}>}
         * @example
         * Playtech.API.responsibleGaming.getPlayerLimitsInfo().then((data) => { ... }));
         */
        getPlayerLimitsInfo,
        /**
         * Set player's deposit limits. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/ResponsibleGamingService/operations/SetPlayerDepositLimitGalaxyNew.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @param {Object} options
         * @param {number} [options.amount]
         * @param {string} [options.timePeriod] Values: 'daily', 'weekly', 'monthly'
         * @param {Object[]} [options.limits]
         * @param {string} [options.limits[].timePeriod] Values: 'daily', 'weekly', 'monthly'
         * @param {string} [options.limits[].amount]
         * @returns {Promise<{Object}>}
         * @example
         * Playtech.API.responsibleGaming.setDepositLimits({ limits: [ { 'timePeriod': 'daily', 'amount' : '101.00' } ]}).then((data) => { ... }));
         * @example
         * Playtech.API.responsibleGaming.setDepositLimits({ amount: '100', timePeriod: 'daily' }).then((data) => { ... }));
         */
        setDepositLimits,
        /**
         * Set player's loss limits. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/ResponsibleGamingService/operations/SetPlayerLossLimits.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @param {Object} options
         * @param {string} options.action
         * @param {Object[]} options.limits
         * @param {string} [options.limits[].timePeriod] Values: 'daily', 'weekly', 'monthly'
         * @param {string} [options.limits[].amount]
         * @returns {Promise<{Object}>}
         * @example
         * Playtech.API.responsibleGaming.setLossLimits().then((data) => { ... }));
         */
        setLossLimits,
        /**
         * Set player's bet limits. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/ResponsibleGamingService/operations/SetPlayerBetLimits.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @param {Object} options
         * @param {string} options.action
         * @param {Object[]} options.limits
         * @param {string} [options.limits[].timePeriod] Values: 'daily', 'weekly', 'monthly'
         * @param {string} [options.limits[].amount]
         * @returns {Promise<{Object}>}
         * @example
         * Playtech.API.responsibleGaming.setLossLimits().then((data) => { ... }));
         */
        setBetLimits,
        /**
         * Set player's self exclusion. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/ResponsibleGamingService/operations/SetPlayerSelfExclusion.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @param {Object} options
         * @param {string} [options.period]
         * @param {string} [options.endDate]
         * @param {string} [options.product]
         * @param {string} [options.endType]
         * @param {string} [options.endSelfExclusion]
         * @param {string} [options.startDate]
         * @param {number} [options.emailNotification]
         * @param {number} [options.networkClass]
         * @returns {Promise<{Object}>}
         * @example
         * Playtech.API.responsibleGaming.setPlayerSelfExclusion({ emailNotification : 3, period : 'openended', product : 'all', endDate: '2018-04-25T17:48:00', endType: 'manual'}).then((data) => { ... }));
         */
        setPlayerSelfExclusion,
        /**
         * See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/GameHistoryService/operations/GameResultsRequest.html}
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @param {Object} options
         * @param {string} options.startDatetime
         * @param {string} options.endDatetime
         * @param {string} options.max
         * @param {string[]} options.fields
         * @param {string} options.consolidateBonusRounds
         * @param {string} options.playerUsername
         * @param {number} options.page
         * @param {string} options.product
         * @param {string} options.gamesSource
         * @param {string[]} options.filterOutParameters
         * @returns {Promise<{Object}>}
         * @example
         * Playtech.API.responsibleGaming.getGameHistory().then((data) => { ... }));
         */
        getGameHistory
    }), [
        getDepositLimits,
        getForcedLossLimits,
        getGameHistory,
        getLossLimits,
        getBetLimits,
        setLossLimits,
        setBetLimits,
        getPlayerLimitsInfo,
        getPlayerSelfExclusion,
        setDepositLimits,
        setPlayerSelfExclusion
    ]);
};
